import React, { useState, useEffect } from 'react';
import { usersApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Users,
  UserCheck,
  UserX,
  Shield,
  ShieldCheck,
  Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const ROLE_CONFIG = {
  admin: { label: 'Administrateur', icon: ShieldCheck, class: 'bg-purple-50 text-purple-700' },
  gestionnaire: { label: 'Gestionnaire', icon: Shield, class: 'bg-blue-50 text-blue-700' },
  lecteur: { label: 'Lecteur', icon: Eye, class: 'bg-slate-100 text-slate-700' }
};

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const { user: currentUser } = useAuth();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await usersApi.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Erreur chargement utilisateurs:', error);
    } finally {
      setLoading(false);
    }
  };

  const openRoleDialog = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setRoleDialogOpen(true);
  };

  const handleRoleUpdate = async () => {
    if (!selectedUser || !newRole) return;
    try {
      await usersApi.updateRole(selectedUser.id, newRole);
      setRoleDialogOpen(false);
      loadUsers();
    } catch (error) {
      console.error('Erreur mise à jour rôle:', error);
    }
  };

  const toggleUserStatus = async (user) => {
    try {
      await usersApi.toggleStatus(user.id);
      loadUsers();
    } catch (error) {
      console.error('Erreur toggle statut:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
          Utilisateurs
        </h1>
        <p className="text-slate-500 mt-1">Gérez les comptes et les droits d'accès</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="card-hover">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-heading font-bold">
                {users.filter(u => u.role === 'admin').length}
              </p>
              <p className="text-sm text-slate-500">Administrateurs</p>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-heading font-bold">
                {users.filter(u => u.role === 'gestionnaire').length}
              </p>
              <p className="text-sm text-slate-500">Gestionnaires</p>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
              <Eye className="w-6 h-6 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-heading font-bold">
                {users.filter(u => u.role === 'lecteur').length}
              </p>
              <p className="text-sm text-slate-500">Lecteurs</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th>Utilisateur</th>
                  <th>Email</th>
                  <th>Rôle</th>
                  <th>Statut</th>
                  <th>Inscrit le</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const roleConfig = ROLE_CONFIG[user.role] || {};
                  const RoleIcon = roleConfig.icon || Eye;
                  const isCurrentUser = user.id === currentUser?.id;

                  return (
                    <tr key={user.id} className="table-row-hover" data-testid={`user-row-${user.id}`}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium">
                            {user.prenom?.charAt(0)}{user.nom?.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium">
                              {user.prenom} {user.nom}
                              {isCurrentUser && (
                                <span className="text-xs text-slate-400 ml-2">(vous)</span>
                              )}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="text-slate-600">{user.email}</td>
                      <td>
                        <Badge className={roleConfig.class}>
                          <RoleIcon className="w-3 h-3 mr-1" />
                          {roleConfig.label}
                        </Badge>
                      </td>
                      <td>
                        {user.is_active ? (
                          <Badge className="bg-emerald-50 text-emerald-700">
                            <UserCheck className="w-3 h-3 mr-1" />
                            Actif
                          </Badge>
                        ) : (
                          <Badge className="bg-rose-50 text-rose-700">
                            <UserX className="w-3 h-3 mr-1" />
                            Inactif
                          </Badge>
                        )}
                      </td>
                      <td className="font-mono text-sm text-slate-500">
                        {new Date(user.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="text-right">
                        {!isCurrentUser && (
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openRoleDialog(user)}
                              data-testid={`change-role-${user.id}`}
                            >
                              Changer rôle
                            </Button>
                            <Button
                              variant={user.is_active ? 'destructive' : 'default'}
                              size="sm"
                              onClick={() => toggleUserStatus(user)}
                              data-testid={`toggle-status-${user.id}`}
                            >
                              {user.is_active ? 'Désactiver' : 'Activer'}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Role Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le rôle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">
              Utilisateur: <span className="font-medium">{selectedUser?.prenom} {selectedUser?.nom}</span>
            </p>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger data-testid="select-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" />
                    Administrateur
                  </div>
                </SelectItem>
                <SelectItem value="gestionnaire">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Gestionnaire
                  </div>
                </SelectItem>
                <SelectItem value="lecteur">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Lecteur
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            <div className="text-xs text-slate-500 space-y-1">
              <p><strong>Administrateur:</strong> Accès complet, gestion des utilisateurs</p>
              <p><strong>Gestionnaire:</strong> Gestion du mobilier, mouvements, inventaires</p>
              <p><strong>Lecteur:</strong> Consultation uniquement</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleRoleUpdate} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-role-btn">
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
